import { Injectable, OnDestroy } from '@angular/core';
import { map } from 'rxjs';

import { UsersService } from '../../Core/Users/services/users.service';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import {
    GroupedAuditableModel,
    LeftSidebarManagementService,
    LhpManagementConfig
} from '../../Common/Layout/services/left-sidebar-management.service';
import { AuditableModel } from '../../Common/Misc/models/auditable.model';
import { DisplayGroupsService } from '../../Common/Layout/services/display-groups.service';
import { DisplayGroupModel } from '../../Common/Layout/models/display-group.model';
import { ActionsEnum } from '../../Common/Basic/enums/actions.enum';
import { Mode } from '../../Common/Layout/enums/mode.enum';
import { ViLeftSidebarService } from './vi-left-sidebar.service';
import { ViMonitorService } from './vi-monitor.service';
import { ViMonitorListItem } from '../models/vi-monitor-list-item.model';
import { ViMonitorStatusEnum } from '../enums/vi-monitor-status.enum';

type ViMonitorRow = GroupedAuditableModel & { status: string };

@Injectable()
export class ViLeftSidebarManagementService extends LeftSidebarManagementService implements OnDestroy {

    private readonly config: LhpManagementConfig = {
        itemSource: this.viMonitorService.all$.pipe(
            map(monitors => monitors.map(monitor => this.toManagementRow(monitor)))
        ),
        columns: [
            this.itemNameColumn,
            ...this.metadataColumns,
            this.statusCustomColumnModel
        ],
        customFilters: [this.statusFilter],
        setUpCustomFilters: () => {
            this.statusFilter.options = [
                { label: 'VisibilityIntelligence.lhp.status.active', value: ViMonitorStatusEnum.active },
                { label: 'VisibilityIntelligence.lhp.status.paused', value: ViMonitorStatusEnum.paused },
                { label: 'VisibilityIntelligence.lhp.status.draft', value: ViMonitorStatusEnum.draft }
            ];
        }
    };

    constructor(
        protected usersService: UsersService,
        public sidebarService: ViLeftSidebarService,
        public displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService,
        private viMonitorService: ViMonitorService,
    ) {
        super(usersService, sidebarService, displayGroupsService, localizationService);

        this.currentMode = Mode.visibilityIntelligence;
        this.currentConfig = this.config;
        this.columnsProvider$.next(this.currentConfig.columns);

        this.subscriptions.push(
            this.config.itemSource.pipe(
                map(items => {
                    this.allListItems = items;
                })
            ).subscribe(() => {
                this.setUpFilters();
            })
        );
    }

    private toManagementRow(monitor: ViMonitorListItem): ViMonitorRow {
        return {
            ...monitor,
            id: monitor.id as unknown as number,
            isFavorite: !!monitor.isFavourite,
            status: monitor.status ?? ViMonitorStatusEnum.paused
        };
    }


    public handleBulkItemAction(items: AuditableModel[], groups: DisplayGroupModel[], action: ActionsEnum): void {
        const ids = items.map(item => item.id);

        switch (action) {
            case ActionsEnum.hide:
            case ActionsEnum.show:
                this.resetSelectionProvider$.next(true);
                this.viMonitorService.bulkUpdateVisibility(ids, action).subscribe();
                break;
            case ActionsEnum.favorite:
                this.resetSelectionProvider$.next(true);
                this.viMonitorService.updateFavourites(ids, []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.resetSelectionProvider$.next(true);
                this.viMonitorService.updateFavourites([], ids).subscribe();
                break;
            case ActionsEnum.move:
                this.loading$.next(true);
                this.displayGroupsService.openGroupSelectionDialog(null, true).subscribe({
                    next: (group: DisplayGroupModel) => {
                        this.resetSelectionProvider$.next(true);
                        // @TODO fix nested subscribe
                        // eslint-disable-next-line rxjs/no-nested-subscribe
                        this.viMonitorService.updateGroup(ids, group).subscribe();
                    },
                    complete: () => this.loading$.next(false)
                });
                break;
            case ActionsEnum.delete:
                this.loading$.next(true);
                this.viMonitorService.confirmBulkDelete(items, groups).subscribe((result) => {
                    this.loading$.next(false);
                    this.resetSelectionProvider$.next(result);
                });

                break;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
